import React, { useContext, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withStyles } from 'theme/withStyles';
import { BrandingContext } from '@glu/interactive-styleguide';
import { CaretLeftIcon } from '@glu/icons-react';

import { MOBILE_SHOW_LOGO } from 'config/ui';
import uiActions from 'core/ui/actions';
import groupActions from 'core/groups/actions';
import pageActions from 'core/page/actions';
import RawHtml from 'common/RawHtml';
import Progress from './Progress';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const styles = ({ palette, screenSizes, typography }) => ({
  backWrapper: {
    display: 'none',
  },
  hidden: {
    display: 'none',
  },
  logo: {
    maxHeight: 90,
    maxWidth: 300,
    width: isIOS ? 'unset' : 'fit-content',
  },
  logoContainer: { display: 'flex', flexDirection: 'column', margin: [0, 'auto'], maxWidth: 450 },
  logoWrapper: {
    minWidth: '48%',
    padding: '2px 0',
  },
  root: {
    alignItems: 'center',
    background: palette.progress.background,
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 100,
    padding: [0, 40],
  },
  subLogoContent: {
    display: 'inline',
  },
  sublogoWrapper: {
    flexDirection: 'column',
  },
  tracker: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 4,
  },
  [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
    backWrapper: {
      '& span': {
        border: 0,
        clip: 'rect(0, 0, 0, 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        width: 1,
      },
      '& svg': {
        fill: typography.progress.color,
      },
      background: 0,
      border: 'none',
      display: 'block',
      height: 16,
      left: 20,
      margin: 0,
      padding: 0,
      position: 'absolute',
      top: 'calc(50% - 8px)',
      width: 16,
    },
    logo: {
      display: 'block',
      margin: [0, 'auto'],
      maxHeight: 60,
      maxWidth: '100%',
      minHeight: 20,
    },
    logoContainer: {
      display: ({ showMobileLogo }) => (showMobileLogo ? 'flex' : 'none'),
    },
    logoWrapper: {
      display: 'block',
      minHeight: 47,
      padding: [12, 58, 15],
      position: 'relative',
      textAlign: 'center',
      width: '100%',
    },
    root: {
      flexDirection: 'column',
      height: 'auto',
      justifyContent: ({ showMobileLogo }) => (showMobileLogo ? 'space-between' : 'flex-end'),
      padding: 0,
    },
    tracker: {
      maxWidth: '100%',
      overflowX: 'auto',
    },
  },
  [`@media(min-width: ${screenSizes.desktopLgMin}px)`]: {
    logoWrapper: {
      width: '50%',
    },
    root: {
      justifyContent: 'center',
    },
    tracker: {
      width: '50%',
    },
  },
});

const Header = ({
  backAllowed,
  backButtonLabel,
  classes,
  clientLabel,
  groupName,
  isLaunch,
  logoFilename,
  onBack,
  pageName,
  progress,
  subLogoContent,
  subLogoURL,
}) => {
  const olRef = useRef();
  const { branding } = useContext(BrandingContext);
  const date = new Date().getTime() || 0;
  useEffect(() => {
    if (classes.root && progress) {
      const indexOfActive = progress.findIndex((p) => p.active);
      olRef.current.scrollLeft = indexOfActive * 206;
    }
  }, [classes.root, progress]);

  const onHandleBack = useCallback(
    (e) => {
      e.preventDefault();
      onBack(isLaunch, groupName);
    },
    [groupName, isLaunch, onBack],
  );

  return (
    <div role="banner" className={classnames('mb-50 header', classes.root)}>
      <div
        className={classnames(
          'logo-wrapper',
          {
            'shown-mobile': MOBILE_SHOW_LOGO.includes(pageName),
          },
          classes.logoWrapper,
        )}
      >
        {classes.backWrapper && backAllowed ? (
          <button type="button" className={classes.backWrapper} onClick={onHandleBack}>
            <CaretLeftIcon />
            <span>{backButtonLabel}</span>
          </button>
        ) : null}
        <div className={classnames(classes.logoContainer)}>
          <img
            src={branding?.logo || `/oao/assets/images/${logoFilename}?v=${date}`}
            className={classnames('logo', classes.logo)}
            alt={clientLabel}
          />
          <div className={classnames(classes.sublogoWrapper)}>
            {subLogoURL && (
              <img
                src={`/oao/assets/images/${subLogoURL}?v=${date}`}
                alt="fdic_logo"
                height="15.74"
              />
            )}
            {subLogoContent && (
              <RawHtml className={classnames(classes.subLogoContent)}>{subLogoContent}</RawHtml>
            )}
          </div>
        </div>
      </div>
      <div
        ref={olRef}
        className={classnames('region-progress-tracker', classes.tracker, {
          [classes.hidden]: !progress?.length,
        })}
      >
        <Progress progress={progress} />
      </div>
    </div>
  );
};

Header.propTypes = {
  backAllowed: PropTypes.bool.isRequired,
  backButtonLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  clientLabel: PropTypes.string,
  groupName: PropTypes.string,
  isLaunch: PropTypes.bool.isRequired,
  logoFilename: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  pageName: PropTypes.string,
  progress: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  showMobileLogo: PropTypes.bool,
  subLogoContent: PropTypes.string,
  subLogoURL: PropTypes.string,
};

Header.defaultProps = {
  backButtonLabel: '',
  clientLabel: '',
  groupName: '',
  logoFilename: 'logo.png',
  pageName: '',
  showMobileLogo: false,
  subLogoContent: undefined,
  subLogoURL: undefined,
};

const mapStateToProps = ({ groups, page, progress, session, ui }) => {
  const groupName = ui.activeGroup;
  const group = groups[groupName];
  const isLaunch = page.name === 'launch';
  return {
    backAllowed:
      (!!groupName &&
        groupName !== 'FUNDING_TRANSFER_EXT' &&
        groupName !== 'FUNDING_TRANSFER_INT' &&
        (page.backAllowed || Object.keys(groups)[0] !== groupName)) ||
      (!!groupName && isLaunch),
    backButtonLabel: group?.backButtonLabel,
    clientLabel: session.data?.clientLabel,
    groupName,
    isLaunch,
    // eslint-disable-next-line camelcase
    logoFilename: session.meta?.configs?.logo_filename,
    pageName: page.name,
    progress,
    showMobileLogo: session.meta?.configs?.mobileLogo,
    subLogoContent: session.meta?.configs?.sub_logo_content,
    subLogoURL: session.meta?.configs?.sub_logo,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onBack: (isLaunch, groupName) => {
    if (isLaunch) {
      dispatch(uiActions.setActiveGroup(''));
    } else if (groupName.startsWith('DONE')) {
      dispatch(pageActions.logout());
    } else if (groupName === 'FUNDING_CC') {
      dispatch(uiActions.setActiveGroup('FUNDING_AMOUNT'));
    } else {
      dispatch(groupActions.back());
    }
  },
});

const HeaderStyled = withStyles(styles)(Header);

export default connect(mapStateToProps, mapDispatchToProps)(HeaderStyled);
